import { useState, type JSX, type ReactNode } from 'react';

/**
 * Phase 3 increment A — the three-column editor shell:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │ Topbar (Space picker, lang, Publish, user menu)      │
 *   ├──────────┬──────────────────────┬────────────────────┤
 *   │ Sidebar  │ Block editor pane    │ Preview iframe     │
 *   │ (Story   │ (Plate.js, fields)   │ (Astro site,       │
 *   │  tree)   │                      │  postMessage sync) │
 *   │          │                      │                    │
 *   └──────────┴──────────────────────┴────────────────────┘
 *
 * Three breakpoints (2026-05-30 responsive-audit-fixer follow-up):
 *  - desktop (md+ = ≥768px): two- or three-column grid as drawn above.
 *  - mobile (default <768px): single-column flex; sidebar collapses
 *    behind a hamburger drawer overlaying the main column; preview is
 *    hidden entirely (the iframe makes no sense on a 375vw viewport).
 *
 * Drawer state:
 *  - Owned by EditorLayout (useState) so the layout stays self-contained.
 *  - Exposed via the `drawerOpenRef` prop so EditorShell can close
 *    the drawer on tab-change without prop-drilling click handlers
 *    down into every sidebar button. Pass `controlled` to switch to a
 *    fully controlled mode when EditorShell wants to dictate the
 *    state — used by the auto-close-on-view-change useEffect.
 */
export interface EditorLayoutProps {
  topbar: ReactNode;
  sidebar: ReactNode;
  /** The middle column — block editor in normal use; asset library / version diff in others. */
  main: ReactNode;
  /** The right column — preview iframe. Optional because not every screen needs preview (e.g. asset library). */
  preview?: ReactNode;
  /**
   * Controlled drawer state. When provided, the layout uses these
   * instead of its internal useState — the consumer can then close
   * the drawer on tab-change via a useEffect.
   */
  drawerOpen?: boolean;
  onDrawerChange?(open: boolean): void;
}

export function EditorLayout({
  topbar,
  sidebar,
  main,
  preview,
  drawerOpen: controlledOpen,
  onDrawerChange,
}: EditorLayoutProps): JSX.Element {
  const hasPreview = preview !== undefined;

  // Allow EditorLayout to manage its own drawer state when the parent
  // doesn't care (e.g. simpler test harnesses), but defer to the
  // controlled props when they're set so EditorShell can auto-close
  // on view-change.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const drawerOpen = controlledOpen ?? uncontrolledOpen;
  const setDrawerOpen = (open: boolean): void => {
    if (onDrawerChange) onDrawerChange(open);
    else setUncontrolledOpen(open);
  };

  return (
    <div
      className="flex h-screen w-screen flex-col text-sm text-zinc-900 md:grid"
      style={{
        // Inline-grid spec runs only at md+ via CSS @media (Tailwind's
        // `md:grid` flips display); the column/row template applies at
        // any width, but flex-col + display:flex wins on mobile.
        gridTemplateRows: '52px 1fr',
        gridTemplateColumns: hasPreview
          ? '280px minmax(0, 1fr) minmax(0, 1.2fr)'
          : '280px minmax(0, 1fr)',
        gridTemplateAreas: hasPreview
          ? '"topbar topbar topbar" "sidebar main preview"'
          : '"topbar topbar" "sidebar main"',
      }}
    >
      <header
        style={{ gridArea: 'topbar' }}
        className="flex h-[52px] shrink-0 items-center gap-2 border-b border-zinc-200 bg-white px-3 md:gap-4 md:px-4"
      >
        {/* Hamburger — mobile only. Toggles the slide-in drawer. */}
        <button
          type="button"
          onClick={() => setDrawerOpen(!drawerOpen)}
          aria-expanded={drawerOpen}
          aria-controls="peacock-admin-drawer"
          aria-label={drawerOpen ? 'Menü schließen' : 'Menü öffnen'}
          data-testid="drawer-toggle"
          className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-100 md:hidden"
        >
          {drawerOpen ? (
            // close (×)
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          ) : (
            // hamburger (≡)
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          )}
        </button>
        {topbar}
      </header>

      <nav
        id="peacock-admin-drawer"
        style={{ gridArea: 'sidebar' }}
        className={
          'fixed inset-y-0 left-0 top-[52px] z-30 flex w-[280px] flex-col gap-2 overflow-y-auto border-r border-zinc-200 bg-zinc-50 px-3 py-4 shadow-xl transition-transform duration-200 ease-out ' +
          'md:static md:top-0 md:z-auto md:shadow-none md:translate-x-0 ' +
          (drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')
        }
        aria-label="Story-Baum"
      >
        {sidebar}
      </nav>

      {/* Backdrop — clicks close the drawer. Mobile only; hidden at md+ */}
      {drawerOpen ? (
        <div
          className="fixed inset-0 top-[52px] z-20 bg-black/40 md:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden
          data-testid="drawer-backdrop"
        />
      ) : null}

      <main
        style={{ gridArea: 'main' }}
        className="min-w-0 flex-1 overflow-y-auto bg-white p-4 md:flex-initial md:p-6"
      >
        {main}
      </main>

      {hasPreview ? (
        <aside
          style={{ gridArea: 'preview' }}
          className="hidden overflow-hidden border-l border-zinc-200 bg-zinc-100 md:block"
          aria-label="Vorschau"
        >
          {preview}
        </aside>
      ) : null}
    </div>
  );
}
